import Cropper from 'cropperjs';
import { useCallback } from 'react';

import type { MNImageContent, MNImageEditorDataContent } from '../../../../../../common/elements/elementModelTypes';
import { asObject } from '../../../../../../common/utils/immutableHelper';
import { DEFAULT_LOSSY_IMAGE_QUALITY } from '../utils/imageModalImageBlobUtils';
import { enrichBlobAsUploadFile } from '../../imageModalFileUtils';

type UploadImageFile = Blob & { name: string; lastModified: number };

type UseCropSaveHandlerOptions = {
    cropperRef: React.RefObject<Cropper>;
    isDirtyRef: React.MutableRefObject<boolean>;
    elementId: string;
    cropSourceImage: MNImageContent;
    imageType: string;
    largestUneditedImageUrl: string;
    hideDrawing: boolean;
    afterSaveCb: () => void;
    getFilenameFromUrl: (url: string, type: string) => string;
    appendToFilename: (filename: string) => string;
    inRange: (min: number, max: number) => (value: number) => boolean;
    // `updateElementImageData` stores crop metadata before the edited upload completes so the element
    // already points at the correct source/original pair for subsequent toggles, revert, and sync.
    updateElementImageData: (
        elementId: string,
        imageOriginal: MNImageContent,
        imageEditorData: MNImageEditorDataContent,
        clearDrawing: boolean,
    ) => void;
    // `revertToOriginalImageData` restores the full source image when the crop rectangle ends up
    // effectively matching the uncropped original again.
    revertToOriginalImageData: (elementId: string, imageOriginal: MNImageContent, clearDrawing: boolean) => void;
    uploadEditedImage: (elementId: string, file: UploadImageFile) => Promise<MNImageContent | void> | void;
    getRevertedImage?: () => MNImageContent | null;
    createAdditionalCroppedImageBlob?: (imageEditorData: Cropper.Data) => Promise<Blob | null>;
    uploadAdditionalCroppedImage?: (
        elementId: string,
        file: UploadImageFile,
        activeRenderedImage?: MNImageContent,
    ) => Promise<MNImageContent | void> | void;
};

/**
 * Persist the crop selection for the active image, and optionally mirror the same crop onto the inactive
 * background-removal variant so both toggle states stay aligned.
 */
export const useCropSaveHandler = ({
    cropperRef,
    isDirtyRef,
    elementId,
    cropSourceImage,
    imageType,
    largestUneditedImageUrl,
    hideDrawing,
    afterSaveCb,
    getFilenameFromUrl,
    appendToFilename,
    inRange,
    updateElementImageData,
    revertToOriginalImageData,
    uploadEditedImage,
    getRevertedImage,
    createAdditionalCroppedImageBlob,
    uploadAdditionalCroppedImage,
}: UseCropSaveHandlerOptions) => {
    const handleSave = useCallback(() => {
        const cropper = cropperRef.current;
        if (!cropper) return;

        if (!isDirtyRef.current) {
            afterSaveCb();
            return;
        }

        const imageData = cropper.getImageData();
        const imageEditorData = cropper.getData();
        const originalImage = asObject<MNImageContent>(cropSourceImage);

        const inWidthRange = inRange(imageData.naturalWidth * 0.99, imageData.naturalWidth * 1.01);
        const inHeightRange = inRange(imageData.naturalHeight * 0.99, imageData.naturalHeight * 1.01);

        const shouldRevertToOriginal =
            originalImage &&
            imageEditorData.rotate === 0 &&
            inWidthRange(imageEditorData.width) &&
            inHeightRange(imageEditorData.height);

        if (shouldRevertToOriginal) {
            const imageToRevert = getRevertedImage?.() || asObject<MNImageContent>(originalImage);
            revertToOriginalImageData(elementId, imageToRevert, hideDrawing);
            afterSaveCb();
            return;
        }

        const blobCreatedCb = async (blob: Blob) => {
            updateElementImageData(elementId, originalImage, imageEditorData, hideDrawing);

            const originalFilename = getFilenameFromUrl(largestUneditedImageUrl, imageType);
            const imageFilename = appendToFilename(originalFilename);

            const enrichedBlob = enrichBlobAsUploadFile(blob, imageFilename);

            try {
                const activeRenderedImage = await uploadEditedImage(elementId, enrichedBlob);

                if (createAdditionalCroppedImageBlob && uploadAdditionalCroppedImage) {
                    // Background removal keeps two variants. When the inactive side already exists,
                    // we crop it with the same selection so toggling still lines up afterwards.
                    const additionalBlob = await createAdditionalCroppedImageBlob(imageEditorData);

                    if (additionalBlob) {
                        await uploadAdditionalCroppedImage(
                            elementId,
                            enrichBlobAsUploadFile(additionalBlob, imageFilename),
                            activeRenderedImage || undefined,
                        );
                    }
                }
            } finally {
                afterSaveCb();
            }
        };

        cropper
            .getCroppedCanvas({
                width: imageEditorData.width,
                height: imageEditorData.height,
                maxWidth: 4096,
                maxHeight: 4096,
            })
            .toBlob(
                (blob: Blob | null) => {
                    if (!blob) return;
                    void blobCreatedCb(blob);
                },
                imageType,
                DEFAULT_LOSSY_IMAGE_QUALITY,
            );
    }, [
        cropperRef,
        isDirtyRef,
        elementId,
        cropSourceImage,
        imageType,
        largestUneditedImageUrl,
        hideDrawing,
        afterSaveCb,
        getFilenameFromUrl,
        appendToFilename,
        inRange,
        updateElementImageData,
        revertToOriginalImageData,
        uploadEditedImage,
        getRevertedImage,
        createAdditionalCroppedImageBlob,
        uploadAdditionalCroppedImage,
    ]);

    return { handleSave };
};
